import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const session_id = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [booking, setBooking] = useState(null);
  const maxAttempts = 15;

  const checkPaymentStatus = useCallback(
    async (attempt = 0) => {
      if (attempt >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API}/bookings/status/${session_id}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setBooking(data.booking);
        } else if (data.status === 'expired') {
          setStatus('failed');
        } else {
          setTimeout(() => checkPaymentStatus(attempt + 1), 2000);
        }
      } catch (error) {
        setStatus('error');
      }
    },
    [session_id]
  );

  useEffect(() => {
    if (session_id) {
      checkPaymentStatus();
    }
  }, [session_id, checkPaymentStatus]);

  return (
    <div className="min-h-screen pt-20 bg-[#050814]">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto">
          {status === 'checking' && (
            <Card data-testid="booking-checking-card" className="border border-[#B8860B]/30 shadow-xl bg-[#0B132B]">
              <CardContent className="p-12 text-center">
                <Loader2 className="w-16 h-16 text-[#B8860B] animate-spin mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-[#F4F1EA] mb-4">Processing Your Payment...</h2>
                <p className="text-[#F4F1EA]/70">
                  Please wait while we confirm your booking. Do not close this window.
                </p>
              </CardContent>
            </Card>
          )}

          {status === 'success' && booking && (
            <Card data-testid="booking-success-card" className="border border-[#B8860B]/30 shadow-xl bg-[#0B132B]">
              <CardHeader className="bg-[#B8860B] text-[#F4F1EA] rounded-t-lg border-b border-[#B8860B]">
                <div className="flex items-center justify-center">
                  <CheckCircle2 className="w-12 h-12 mr-3 text-[#B8860B]" />
                  <CardTitle className="text-3xl">Booking Confirmed!</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-8">
                <div className="mb-8 text-center">
                  <p className="text-lg text-[#F4F1EA] mb-2">
                    Thank you for your booking at Creek Bottom Ranch &amp; Resort
                  </p>
                  <p className="text-sm text-[#F4F1EA]/70">
                    A confirmation email has been sent to {booking.email}
                  </p>
                </div>

                <div className="space-y-4 mb-8">
                  {[
                    ['Booking ID:', booking.id.substring(0, 8).toUpperCase()],
                    ['Site Type:', booking.site_type],
                    ['Check-in:', new Date(booking.check_in).toLocaleDateString()],
                    ['Check-out:', new Date(booking.check_out).toLocaleDateString()],
                    ['Guests:', booking.guests],
                  ].map(([label, value], idx) => (
                    <div key={idx} className="flex justify-between py-3 border-b border-[#B8860B]/20">
                      <span className="font-semibold text-[#B8860B]">{label}</span>
                      <span className="text-[#F4F1EA]">{value}</span>
                    </div>
                  ))}
                  <div className="flex justify-between py-3 border-b-2 border-[#B8860B]">
                    <span className="font-bold text-[#F4F1EA] text-lg">Total Paid:</span>
                    <span className="font-bold text-[#B8860B] text-lg">${booking.total_amount.toFixed(2)}</span>
                  </div>
                </div>

                <div className="bg-[#050814] border border-[#B8860B]/20 p-6 rounded-lg mb-6">
                  <h3 className="font-bold text-[#B8860B] mb-3">What's Next?</h3>
                  <ul className="space-y-2 text-sm text-[#F4F1EA]/80">
                    <li>• Check your email for detailed confirmation and directions</li>
                    <li>• Check-in time is 2:00 PM on your arrival date</li>
                    <li>• Check-out time is 11:00 AM on your departure date</li>
                    <li>• Contact us at (832) 714-0101 if you have any questions</li>
                  </ul>
                </div>

                <div className="flex gap-4">
                  <Link to="/" className="flex-1">
                    <Button
                      data-testid="booking-success-home-btn"
                      className="w-full bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA]"
                    >
                      Return Home
                    </Button>
                  </Link>
                  <Link to="/contact" className="flex-1">
                    <Button
                      data-testid="booking-success-contact-btn"
                      variant="outline"
                      className="w-full border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-[#050814]"
                    >
                      Contact Us
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {status === 'timeout' && (
            <Card data-testid="booking-pending-card" className="border border-[#B8860B]/30 shadow-xl bg-[#0B132B]">
              <CardContent className="p-12 text-center">
                <Loader2 className="w-16 h-16 text-[#B8860B] mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-[#F4F1EA] mb-4">
                  Payment received — finalizing your booking
                </h2>
                <p className="text-[#F4F1EA]/80 mb-2">
                  Stripe is taking a little longer than usual to confirm. Your card has been charged successfully.
                </p>
                <p className="text-[#F4F1EA]/70 mb-8">
                  Refresh this page in a moment to see your confirmation, or check your email — both will arrive shortly.
                </p>
                <div className="flex gap-4 justify-center flex-wrap">
                  <Button
                    onClick={() => window.location.reload()}
                    className="bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA] font-semibold"
                  >
                    Refresh Now
                  </Button>
                  <Link to="/contact">
                    <Button
                      variant="outline"
                      className="border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-[#050814]"
                    >
                      Contact Us
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {(status === 'failed' || status === 'error') && (
            <Card data-testid="booking-failed-card" className="border border-[#B8860B]/30 shadow-xl bg-[#0B132B]">
              <CardContent className="p-12 text-center">
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-[#F4F1EA] mb-4">Payment Failed</h2>
                <p className="text-[#F4F1EA]/70 mb-8">
                  There was an issue processing your payment. Please try again or contact us for assistance.
                </p>
                <div className="flex gap-4 justify-center">
                  <Link to="/booking">
                    <Button className="bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#050814]">
                      Try Again
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button
                      variant="outline"
                      className="border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-[#050814]"
                    >
                      Contact Support
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
